import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

import { TransitionEffect } from "~/common/components";
import {
  CompletedListPastMedicationsAnswer,
  ListPastMedicationQuestionData,
  ScreeningListPastMedicationsAnswer,
} from "~/common/utils";
import {
  AddElementButton,
  ConsultationQuestionsLayout,
} from "~/patients/components/consultations/shared";
import { listPastMedicationSchema } from "~/patients/utils";
import {
  PastMedicationInput,
  PastMedicationInputValues,
} from "./inputs/PastMedicationInput";

export type ListPastMedicationFormValues = z.infer<
  typeof listPastMedicationSchema
>;

interface ListPastPyschMedicationsFormProps {
  question: ListPastMedicationQuestionData;
  handleGoBack: () => void;
  onSubmit: (answer: ScreeningListPastMedicationsAnswer) => void;
  isSubmitting: boolean;
  disableBack: boolean;
  completedAnswers: CompletedListPastMedicationsAnswer;
}

export const ListPastPyschMedicationsForm = ({
  completedAnswers,
  disableBack,
  handleGoBack,
  isSubmitting,
  onSubmit,
  question,
}: ListPastPyschMedicationsFormProps) => {
  const emptyMedication = { name: "" };
  const { title, subtitle } = question;

  const {
    handleSubmit,
    watch,
    register,
    control,
    formState: { errors },
  } = useForm<ListPastMedicationFormValues>({
    defaultValues: { answer: { ...completedAnswers } },
    resolver: zodResolver(listPastMedicationSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "answer.medications",
  });

  const saveAnswers: SubmitHandler<ListPastMedicationFormValues> = (data) =>
    onSubmit(data);

  const emptyMedicationInput = (index: number) =>
    index > fields.length || !watch(`answer.medications.${index}.name`);

  const medications = watch("answer.medications") || [];
  const disableNext = medications.length === 0;

  return (
    <ConsultationQuestionsLayout
      handleNext={handleSubmit(saveAnswers)}
      handleBack={handleGoBack}
      disableNext={disableNext}
      isSubmitting={isSubmitting}
      title={title}
      subtitle={subtitle}
      disableBack={disableBack}
    >
      {fields.map((item, index) => (
        <TransitionEffect key={item.id}>
          <PastMedicationInput
            handleDelete={() => remove(index)}
            isMedicationDataEmpty={emptyMedicationInput(index)}
            register={(data: keyof PastMedicationInputValues) =>
              register(`answer.medications.${index}.${data}`)
            }
            errors={errors.answer?.medications?.[index]}
          />
        </TransitionEffect>
      ))}
      <AddElementButton
        onClick={() => append(emptyMedication)}
        title="Add medication"
      />
    </ConsultationQuestionsLayout>
  );
};
